/**
 * IEP/504-style supports editor — stores actions (time, pacing), not disability labels.
 */
import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';

const TIME_PRESETS = [
  { value: 1, label: 'Standard time (100%)' },
  { value: 1.5, label: 'Time-and-a-half (150%)' },
  { value: 2, label: 'Double time (200%)' }
];

export default function StudentAccommodationDialog({
  open,
  onClose,
  student,
  sectionId,
  disabled = false,
  onSaved
}) {
  const [timeMultiplier, setTimeMultiplier] = useState(1);
  const [untimedAssessments, setUntimedAssessments] = useState(false);
  const [extraAttempts, setExtraAttempts] = useState(0);
  const [alternatePacing, setAlternatePacing] = useState(false);
  const [idleGraceDays, setIdleGraceDays] = useState('');
  const [onTrackPct, setOnTrackPct] = useState('');
  const [lowMasteryPct, setLowMasteryPct] = useState('');
  const [teacherNote, setTeacherNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !student) return;
    const a = student.accommodation || {};
    setTimeMultiplier(Number(a.timeMultiplier) > 1 ? Number(a.timeMultiplier) : 1);
    setUntimedAssessments(Boolean(a.untimedAssessments));
    setExtraAttempts(Number(a.extraAttempts) || 0);
    setAlternatePacing(Boolean(a.alternatePacing));
    setIdleGraceDays(a.idleGraceDays != null ? String(a.idleGraceDays) : '');
    setOnTrackPct(a.onTrackPct != null ? String(a.onTrackPct) : '');
    setLowMasteryPct(a.lowMasteryPct != null ? String(a.lowMasteryPct) : '');
    setTeacherNote(a.teacherNote || '');
    setError('');
  }, [open, student]);

  const save = async (clear = false) => {
    if (!student || !sectionId || disabled) return;
    setBusy(true);
    setError('');
    try {
      const body = clear
        ? { clear: true }
        : {
            timeMultiplier: untimedAssessments ? 1 : Number(timeMultiplier) || 1,
            untimedAssessments,
            extraAttempts: Number(extraAttempts) || 0,
            alternatePacing,
            idleGraceDays: idleGraceDays === '' ? null : Number(idleGraceDays),
            onTrackPct: onTrackPct === '' ? null : Number(onTrackPct),
            lowMasteryPct: lowMasteryPct === '' ? null : Number(lowMasteryPct),
            teacherNote: teacherNote.trim()
          };
      const r = await fetch(
        `/api/institutions/active/sections/${sectionId}/accommodations/${student.userId}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        }
      );
      const json = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(json.error ? String(json.error).replace(/_/g, ' ') : 'Could not save supports');
        return;
      }
      if (typeof onSaved === 'function') onSaved(student.userId, json.accommodation || null);
      onClose();
    } catch {
      setError('Network error saving supports');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>
        Assessment supports
        {student?.name ? (
          <>
            {' · '}
            <Box component="span" className="skylite-privacy-blur" data-skylite-privacy="blur">
              {student.name}
            </Box>
          </>
        ) : null}
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Enter the same supports from an IEP/504 (time-and-a-half, double time, pacing). We store actions
          only — not disability labels — and use them for intervention flags and timed assessments.
        </Alert>
        {error ? (
          <Typography variant="body2" sx={{ color: '#fb923c', mb: 1 }}>
            {error}
          </Typography>
        ) : null}
        <Stack spacing={1.5} sx={{ mt: 0.5 }}>
          <TextField
            select
            size="small"
            label="Extended time"
            value={timeMultiplier}
            onChange={(e) => setTimeMultiplier(Number(e.target.value))}
            disabled={untimedAssessments || disabled}
          >
            {TIME_PRESETS.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Checkbox
                checked={untimedAssessments}
                onChange={(e) => setUntimedAssessments(e.target.checked)}
                disabled={disabled}
              />
            }
            label="Untimed assessments"
          />
          <TextField
            size="small"
            type="number"
            label="Extra quiz attempts"
            value={extraAttempts}
            onChange={(e) => setExtraAttempts(e.target.value)}
            inputProps={{ min: 0, max: 10 }}
            disabled={disabled}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={alternatePacing}
                onChange={(e) => setAlternatePacing(e.target.checked)}
                disabled={disabled}
              />
            }
            label="Alternate pacing (skip median comparison; longer idle grace)"
          />
          <TextField
            size="small"
            type="number"
            label="Custom idle grace (days, optional)"
            value={idleGraceDays}
            onChange={(e) => setIdleGraceDays(e.target.value)}
            placeholder="Class default"
            disabled={disabled}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              size="small"
              type="number"
              label="Personal on-track % (optional)"
              value={onTrackPct}
              onChange={(e) => setOnTrackPct(e.target.value)}
              placeholder="Class default"
              fullWidth
              disabled={disabled}
            />
            <TextField
              size="small"
              type="number"
              label="Personal low-mastery % (optional)"
              value={lowMasteryPct}
              onChange={(e) => setLowMasteryPct(e.target.value)}
              placeholder="Class default"
              fullWidth
              disabled={disabled}
            />
          </Stack>
          <TextField
            size="small"
            multiline
            minRows={2}
            label="Teacher note (private)"
            value={teacherNote}
            onChange={(e) => setTeacherNote(e.target.value)}
            placeholder="e.g. IEP on file with counselor — 200% time"
            disabled={disabled}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button color="inherit" onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button color="warning" onClick={() => save(true)} disabled={busy || disabled} sx={{ textTransform: 'none' }}>
          Clear supports
        </Button>
        <Button variant="contained" onClick={() => save(false)} disabled={busy || disabled} sx={{ textTransform: 'none', fontWeight: 800 }}>
          Save supports
        </Button>
      </DialogActions>
    </Dialog>
  );
}
